import type { EchoflicksSessionFramingEvent } from "../types.js";
import { correlateFeedbackSceneCandidates } from "../echoflicks/framingDiagnostics.js";
import type { FeedbackJsonObject } from "./feedbackContracts.js";

/**
 * Frozen framing correlation captured when crop feedback is created
 * (CROP-WB-003). The snapshot points permanently to the evidence the
 * operator was viewing, so a later retry or re-index can never silently
 * remap the report to different pixels.
 *
 * Minimized by design: correlation identifiers and digests only. Never a
 * signed URL, object key, raw prompt, filename, person name, interview
 * answer, or pixel geometry.
 */

export const FEEDBACK_FRAMING_SNAPSHOT_SCHEMA_VERSION =
  "feedback-framing-correlation.v1" as const;

export type FeedbackFramingCorrelationMethod =
  | "inspector_exact"
  | "timeline_derived"
  | "none";

export type FeedbackFramingCorrelationStatus =
  | "exact"
  | "derived"
  | "ambiguous"
  | "unmatched"
  | "stale";

export type FeedbackFramingSnapshot = {
  schemaVersion: typeof FEEDBACK_FRAMING_SNAPSHOT_SCHEMA_VERSION;
  correlationMethod: FeedbackFramingCorrelationMethod;
  correlationStatus: FeedbackFramingCorrelationStatus;
  /** Half-open boundary rule used for the interval match. */
  boundaryMethod: "half_open_start_inclusive";
  sceneId: string | null;
  sceneStartSeconds: number | null;
  sceneEndSeconds: number | null;
  /** Position inside the scene; final-video absolute time stays in position. */
  sceneRelativeSeconds: number | null;
  attemptId: string | null;
  taskId: string | null;
  planId: string | null;
  planDigest: string | null;
  planRevision: number | null;
  appliedReceiptId: string | null;
  appliedReceiptDigest: string | null;
  configReleaseId: string | null;
  configDigest: string | null;
  deliveredAssetSha256: string | null;
  selectedStage: string | null;
  candidateSceneIds: string[];
};

/** Exact selection handed over by the already-authorized scene inspector. */
export type FramingInspectorSelection = {
  sceneId: string;
  attemptId: string | null;
  taskId?: string | null;
  planId?: string | null;
  planDigest?: string | null;
  planRevision?: number | null;
  appliedReceiptId?: string | null;
  appliedReceiptDigest?: string | null;
  configReleaseId?: string | null;
  configDigest?: string | null;
  deliveredAssetSha256?: string | null;
  selectedStage?: string | null;
  sceneStartSeconds?: number | null;
  sceneEndSeconds?: number | null;
};

const SNAPSHOT_KEYS = [
  "schemaVersion",
  "correlationMethod",
  "correlationStatus",
  "boundaryMethod",
  "sceneId",
  "sceneStartSeconds",
  "sceneEndSeconds",
  "sceneRelativeSeconds",
  "attemptId",
  "taskId",
  "planId",
  "planDigest",
  "planRevision",
  "appliedReceiptId",
  "appliedReceiptDigest",
  "configReleaseId",
  "configDigest",
  "deliveredAssetSha256",
  "selectedStage",
  "candidateSceneIds",
] as const;

const METHODS: readonly FeedbackFramingCorrelationMethod[] = [
  "inspector_exact",
  "timeline_derived",
  "none",
];

const STATUSES: readonly FeedbackFramingCorrelationStatus[] = [
  "exact",
  "derived",
  "ambiguous",
  "unmatched",
  "stale",
];

const MAX_ID_LENGTH = 200;
const MAX_CANDIDATES = 50;
const FORBIDDEN_VALUE = /https?:\/\/|x-amz-|x-goog-|data:image|data:video|\//i;

function safeId(value: unknown, field: string): string | null {
  if (value == null) return null;
  const text = typeof value === "number" ? String(value) : value;
  if (typeof text !== "string") {
    throw new TypeError(`Framing snapshot ${field} must be a string`);
  }
  const trimmed = text.trim();
  if (!trimmed) return null;
  if (trimmed.length > MAX_ID_LENGTH) {
    throw new TypeError(`Framing snapshot ${field} is too long`);
  }
  if (FORBIDDEN_VALUE.test(trimmed)) {
    throw new TypeError(
      `Framing snapshot ${field} must be an identifier, not a path or URL`,
    );
  }
  return trimmed;
}

function safeSeconds(value: unknown, field: string): number | null {
  if (value == null) return null;
  if (
    typeof value !== "number" ||
    !Number.isFinite(value) ||
    value < 0 ||
    value > 86_400
  ) {
    throw new TypeError(`Framing snapshot ${field} is out of range`);
  }
  return value;
}

function safeRevision(value: unknown): number | null {
  if (value == null) return null;
  if (!Number.isInteger(value) || (value as number) < 0) {
    throw new TypeError("Framing snapshot planRevision is invalid");
  }
  return value as number;
}

/**
 * Strict, fail-closed parser. Unknown keys, URLs, paths, or oversized values
 * are rejected rather than stored.
 */
export function parseFeedbackFramingSnapshot(
  value: unknown,
): FeedbackFramingSnapshot {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    throw new TypeError("Framing snapshot must be an object");
  }
  const record = value as Record<string, unknown>;
  const allowed = new Set<string>(SNAPSHOT_KEYS);
  for (const key of Object.keys(record)) {
    if (!allowed.has(key)) {
      throw new TypeError(`Framing snapshot has unknown field ${key}`);
    }
  }
  if (record.schemaVersion !== FEEDBACK_FRAMING_SNAPSHOT_SCHEMA_VERSION) {
    throw new TypeError("Unsupported framing snapshot schema version");
  }
  if (!METHODS.includes(record.correlationMethod as never)) {
    throw new TypeError("Invalid framing snapshot correlation method");
  }
  if (!STATUSES.includes(record.correlationStatus as never)) {
    throw new TypeError("Invalid framing snapshot correlation status");
  }
  if (record.boundaryMethod !== "half_open_start_inclusive") {
    throw new TypeError("Invalid framing snapshot boundary method");
  }
  const candidates = record.candidateSceneIds;
  if (!Array.isArray(candidates) || candidates.length > MAX_CANDIDATES) {
    throw new TypeError("Framing snapshot candidate list is invalid");
  }
  return {
    schemaVersion: FEEDBACK_FRAMING_SNAPSHOT_SCHEMA_VERSION,
    correlationMethod:
      record.correlationMethod as FeedbackFramingCorrelationMethod,
    correlationStatus:
      record.correlationStatus as FeedbackFramingCorrelationStatus,
    boundaryMethod: "half_open_start_inclusive",
    sceneId: safeId(record.sceneId, "sceneId"),
    sceneStartSeconds: safeSeconds(
      record.sceneStartSeconds,
      "sceneStartSeconds",
    ),
    sceneEndSeconds: safeSeconds(record.sceneEndSeconds, "sceneEndSeconds"),
    sceneRelativeSeconds: safeSeconds(
      record.sceneRelativeSeconds,
      "sceneRelativeSeconds",
    ),
    attemptId: safeId(record.attemptId, "attemptId"),
    taskId: safeId(record.taskId, "taskId"),
    planId: safeId(record.planId, "planId"),
    planDigest: safeId(record.planDigest, "planDigest"),
    planRevision: safeRevision(record.planRevision),
    appliedReceiptId: safeId(record.appliedReceiptId, "appliedReceiptId"),
    appliedReceiptDigest: safeId(
      record.appliedReceiptDigest,
      "appliedReceiptDigest",
    ),
    configReleaseId: safeId(record.configReleaseId, "configReleaseId"),
    configDigest: safeId(record.configDigest, "configDigest"),
    deliveredAssetSha256: safeId(
      record.deliveredAssetSha256,
      "deliveredAssetSha256",
    ),
    selectedStage: safeId(record.selectedStage, "selectedStage"),
    candidateSceneIds: candidates.map((item, index) => {
      const id = safeId(item, `candidateSceneIds[${index}]`);
      if (id === null) {
        throw new TypeError("Framing snapshot candidate ids must be non-empty");
      }
      return id;
    }),
  };
}

const EMPTY_SNAPSHOT: FeedbackFramingSnapshot = {
  schemaVersion: FEEDBACK_FRAMING_SNAPSHOT_SCHEMA_VERSION,
  correlationMethod: "none",
  correlationStatus: "unmatched",
  boundaryMethod: "half_open_start_inclusive",
  sceneId: null,
  sceneStartSeconds: null,
  sceneEndSeconds: null,
  sceneRelativeSeconds: null,
  attemptId: null,
  taskId: null,
  planId: null,
  planDigest: null,
  planRevision: null,
  appliedReceiptId: null,
  appliedReceiptDigest: null,
  configReleaseId: null,
  configDigest: null,
  deliveredAssetSha256: null,
  selectedStage: null,
  candidateSceneIds: [],
};

/**
 * Freeze the correlation the operator was actually looking at. An
 * authorized inspector selection produces an exact snapshot; otherwise the
 * delivered timeline yields derived/ambiguous/unmatched — never a guessed
 * attempt.
 */
export function buildFeedbackFramingSnapshot(params: {
  timestampMs: number;
  framingEvents?: readonly EchoflicksSessionFramingEvent[] | null;
  inspectorSelection?: FramingInspectorSelection | null;
}): FeedbackFramingSnapshot {
  const timestampSeconds = params.timestampMs / 1_000;
  const selection = params.inspectorSelection;
  if (selection && selection.sceneId) {
    const start = selection.sceneStartSeconds ?? null;
    const end = selection.sceneEndSeconds ?? null;
    return {
      ...EMPTY_SNAPSHOT,
      correlationMethod: "inspector_exact",
      correlationStatus: "exact",
      sceneId: String(selection.sceneId),
      sceneStartSeconds: start,
      sceneEndSeconds: end,
      sceneRelativeSeconds:
        start !== null && timestampSeconds >= start
          ? timestampSeconds - start
          : null,
      attemptId: selection.attemptId ?? null,
      taskId: selection.taskId ?? null,
      planId: selection.planId ?? null,
      planDigest: selection.planDigest ?? null,
      planRevision: selection.planRevision ?? null,
      appliedReceiptId: selection.appliedReceiptId ?? null,
      appliedReceiptDigest: selection.appliedReceiptDigest ?? null,
      configReleaseId: selection.configReleaseId ?? null,
      configDigest: selection.configDigest ?? null,
      deliveredAssetSha256: selection.deliveredAssetSha256 ?? null,
      selectedStage: selection.selectedStage ?? null,
      candidateSceneIds: [String(selection.sceneId)],
    };
  }
  const events = params.framingEvents ?? [];
  if (events.length === 0) return { ...EMPTY_SNAPSHOT };
  const candidates = correlateFeedbackSceneCandidates(
    [...events],
    timestampSeconds,
  );
  if (candidates.status !== "derived") {
    return {
      ...EMPTY_SNAPSHOT,
      correlationMethod: "timeline_derived",
      correlationStatus: candidates.status,
      candidateSceneIds: candidates.candidateSceneIds,
    };
  }
  const correlation = candidates.correlation;
  const interval = sceneInterval(events, correlation.sceneId);
  return {
    ...EMPTY_SNAPSHOT,
    correlationMethod: "timeline_derived",
    correlationStatus: "derived",
    sceneId: correlation.sceneId,
    sceneStartSeconds: interval?.start ?? null,
    sceneEndSeconds: interval?.end ?? null,
    sceneRelativeSeconds:
      interval && timestampSeconds >= interval.start
        ? timestampSeconds - interval.start
        : null,
    attemptId: correlation.attemptId,
    taskId: correlation.taskId,
    planId: correlation.framingPlanId,
    planDigest: correlation.planDigest,
    candidateSceneIds: candidates.candidateSceneIds,
  };
}

function sceneInterval(
  events: readonly EchoflicksSessionFramingEvent[],
  sceneId: string | null,
): { start: number; end: number } | null {
  if (sceneId === null) return null;
  for (const event of events) {
    if (String(event.scene_id) !== sceneId) continue;
    const payload = event.payload ?? {};
    const start =
      event.timeline_start_seconds ?? payload.timeline_start_seconds;
    const end = event.timeline_end_seconds ?? payload.timeline_end_seconds;
    if (
      typeof start === "number" &&
      typeof end === "number" &&
      Number.isFinite(start) &&
      Number.isFinite(end) &&
      end > start
    ) {
      return { start, end };
    }
  }
  return null;
}

/**
 * Server-side consistency check before persisting (CROP-WB-003). A snapshot
 * whose frozen scene interval no longer contains the reported timestamp is
 * persisted as an explicit stale snapshot — never silently remapped.
 */
export function reconcileFramingSnapshot(
  snapshot: FeedbackFramingSnapshot,
  timestampMs: number,
): { snapshot: FeedbackFramingSnapshot; downgraded: boolean } {
  if (
    snapshot.correlationStatus !== "exact" &&
    snapshot.correlationStatus !== "derived"
  ) {
    return { snapshot, downgraded: false };
  }
  const seconds = timestampMs / 1_000;
  const { sceneStartSeconds: start, sceneEndSeconds: end } = snapshot;
  if (start === null || end === null) return { snapshot, downgraded: false };
  const inside = seconds >= start && seconds < end;
  const finalFrameTolerance = 0.001;
  const atFinalBoundary = Math.abs(seconds - end) <= finalFrameTolerance;
  if (inside || atFinalBoundary) return { snapshot, downgraded: false };
  return {
    snapshot: { ...snapshot, correlationStatus: "stale" },
    downgraded: true,
  };
}

export function framingSnapshotToJson(
  snapshot: FeedbackFramingSnapshot,
): FeedbackJsonObject {
  return { snapshot: { ...snapshot } } as unknown as FeedbackJsonObject;
}

export function framingSnapshotFromMetadataSection(
  section: unknown,
): FeedbackFramingSnapshot | null {
  if (
    typeof section !== "object" ||
    section === null ||
    Array.isArray(section)
  ) {
    return null;
  }
  const raw = (section as Record<string, unknown>).snapshot;
  if (raw == null) return null;
  try {
    return parseFeedbackFramingSnapshot(raw);
  } catch {
    return null;
  }
}

type MetadataWithFraming = {
  framing?: FeedbackJsonObject;
  collectors: Record<string, { status: string; missing: string[] } | undefined>;
};

/**
 * Server-side validation before persisting (CROP-WB-003). A malformed
 * snapshot is dropped (the operator comment always survives); a snapshot
 * whose frozen interval no longer contains the reported timestamp is
 * persisted explicitly stale. Nothing is silently remapped.
 */
export function reconcileSubmittedFramingMetadata<
  T extends MetadataWithFraming,
>(metadata: T, timestampMs: number): T {
  if (metadata.framing === undefined) return metadata;
  const raw = (metadata.framing as Record<string, unknown>).snapshot;
  if (raw == null) return metadata;
  let parsed: FeedbackFramingSnapshot;
  try {
    parsed = parseFeedbackFramingSnapshot(raw);
  } catch {
    return {
      ...metadata,
      framing: {},
      collectors: {
        ...metadata.collectors,
        framing: {
          status: "unavailable",
          missing: ["framing.snapshot_invalid"],
        },
      },
    };
  }
  const { snapshot, downgraded } = reconcileFramingSnapshot(
    parsed,
    timestampMs,
  );
  if (!downgraded) {
    return { ...metadata, framing: framingSnapshotToJson(snapshot) };
  }
  const previous = metadata.collectors.framing;
  return {
    ...metadata,
    framing: framingSnapshotToJson(snapshot),
    collectors: {
      ...metadata.collectors,
      framing: {
        status: "partial",
        missing: [
          ...new Set([
            ...(previous?.missing ?? []),
            "framing.snapshot_stale_interval",
          ]),
        ],
      },
    },
  };
}
